import { useEffect, useRef } from 'react';
import LightRays from '../components/LightRays';
import LaserFlow from '../components/LaserFlow';
import TiltedCard from '../components/TiltedCard';

const CARDS = [
  { num: '01', label: 'Mission', accent: '#ec2b28', heading: 'Move brands forward.', body: 'We cut through noise and create real momentum — through strategy, design, and execution that actually connects with people.' },
  { num: '02', label: 'Vision', accent: '#ec2b28', heading: 'Where great ideas win.', body: 'Bold thinking and craft should always outperform budget and legacy. We build the creative landscape that makes that possible.' },
  { num: '03', label: 'Promise', accent: '#ec2b28', heading: 'We show up. Every time.', body: 'Full commitment, honest strategy, and work we are proud to put our name on — no exceptions, no excuses.' },
];

const ABOUT_LINES = [
  { words: ['Aurelix', 'is', 'a', 'consulting', '&', 'growth', 'firm'] },
  { words: ['We', 'connect', 'ads,', 'funnels,', 'and', 'operations'] },
  { words: ['to', 'deliver', 'measurable', 'growth', 'that', 'matters'] },
];

function lerp(a, b, t) { return a + (b - a) * t; }
function eio(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
function c01(v) { return Math.max(0, Math.min(1, v)); }

export default function About() {
  const zoneRef = useRef(null);
  const cardRefs = useRef([]);
  const lineRefs = useRef([]);
  const calcRefs = useRef([]);
  const wrapRefs = useRef([]);
  const rafRef = useRef(null);
  const smRef = useRef(0);

  useEffect(() => {
    const zone = zoneRef.current;
    if (!zone) return;

    const tick = () => {
      if (window.innerWidth <= 767) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const rect = zone.getBoundingClientRect();
      const total = zone.offsetHeight - window.innerHeight;
      const raw = total > 0 ? c01(-rect.top / total) : 0;
      smRef.current += (raw - smRef.current) * 0.07;
      const t = eio(smRef.current);
      const vw = window.innerWidth;

      const leftEl = document.querySelector('.ab-left-col');
      const leftW = leftEl ? leftEl.offsetWidth * 0.92 : vw * 0.42;

      lineRefs.current.forEach((line, i) => {
        if (!line) return;
        const calc = calcRefs.current[i];
        if (!calc) return;
        const wrap = wrapRefs.current[i];
        const nw = calc.offsetWidth;
        const sw = Math.max(nw, leftW);
        let cp = 0, ty = 0, op = 1;
        if (i === 0) { cp = c01(t / 0.45); }
        else if (i === 1) { const p = c01((t - 0.15) / 0.5); cp = p; ty = (1 - p) * 40; op = c01((t - 0.15) / 0.25); }
        else { const p = c01((t - 0.3) / 0.5); cp = p; ty = (1 - p) * 60; op = c01((t - 0.3) / 0.25); }
        line.style.width = `${sw - (sw - nw) * cp}px`;
        if (wrap) { wrap.style.transform = `translateY(${ty}px)`; wrap.style.opacity = String(op); }
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap');

        @keyframes aboutHint  { 0%,100%{opacity:.12} 50%{opacity:.5} }
        @keyframes blurReveal {
          from { opacity:0; filter:blur(14px); transform:translateY(8px); }
          to   { opacity:1; filter:blur(0);    transform:translateY(0); }
        }

        .ab-calc-span {
          position:absolute; visibility:hidden; pointer-events:none;
          font-family:'Bricolage Grotesque',sans-serif;
          font-size:clamp(1rem,2vw,1.9rem);
          font-weight:600; text-transform:uppercase; white-space:nowrap;
        }
        .ab-visible-line {
          display:flex; justify-content:space-between; align-items:center;
          white-space:nowrap; box-sizing:border-box; overflow:hidden;
          font-family:'Bricolage Grotesque',sans-serif;
          font-size:clamp(1rem,2vw,1.9rem);
          font-weight:600;
          letter-spacing:-0.02em; line-height:1.3; color:#fff; will-change:width;
        }
        .ab-line-wrap {
          width:100%; display:flex; justify-content:flex-start; position:relative;
        }

        /* ══════════════════════════
           DESKTOP (768px+)
        ══════════════════════════ */
        @media (min-width:768px) {
          .ab-mobile-header { display:none !important; }
          .ab-mob-list      { display:none !important; }

          .ab-zone {
            position: relative;
            height: 350vh;
            background: #0a0a0a;
            color: #fff;
          }

          .ab-sticky {
            position: sticky;
            top: 0;
            height: 100vh;
            display: flex;
            flex-direction: column;
            background: #0a0a0a;
            border-top: 1px solid rgba(255,255,255,0.07);
            overflow: hidden;
          }

          .ab-desk-header {
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            padding: clamp(24px,4vh,44px) clamp(24px,4vw,64px) 0;
            flex-shrink: 0;
          }
          .ab-desk-heading {
            font-family: 'Bricolage Grotesque', sans-serif;
            font-size: clamp(2.4rem, 5.5vw, 4.5rem);
            font-weight: 800;
            letter-spacing: -0.035em;
            line-height: 1.08;
            text-transform: none;
            color: #ec2b28;
            margin: 0;
            text-align: center;
          }
          .ab-desk-foundation {
            position: absolute;
            right: clamp(24px,4vw,64px);
            top: 50%;
            transform: translateY(-50%);
            font-size: 0.55rem;
            letter-spacing: 0.28em;
            text-transform: uppercase;
            color: rgba(255,255,255,0.22);
            font-family: 'Bricolage Grotesque', sans-serif;
          }

          .ab-desk-body {
            flex: 1;
            display: flex;
            align-items: stretch;
            min-height: 0;
          }

          .ab-left-col {
            flex: 0 0 50%;
            width: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 0 clamp(28px,4vw,64px);
            position: relative;
            z-index: 1;
            border-right: 1px solid rgba(255,255,255,0.06);
            box-sizing: border-box;
          }
          .ab-left-lines {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .ab-right-col {
            flex: 0 0 50%;
            width: 50%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 0 clamp(20px,3vw,48px);
            box-sizing: border-box;
            position: relative;
            z-index: 1;
            overflow: hidden;
          }

          /* LaserFlow bg inside right col */
          .ab-right-laser {
            position: absolute;
            inset: 0;
            z-index: 0;
            pointer-events: none;
            opacity: 0.55;
          }

          .ab-desk-card-list {
            display: grid;
            grid-template-columns: 1fr;
        --dcard-h: clamp(150px, 18vh, 210px);
            --dcard-offset: 36px;
            grid-template-rows: repeat(3, var(--dcard-h));
            gap: 25px;
            list-style: none;
            margin: 0;
            padding: 0;
            position: relative;
            z-index: 1;
          }

          .ab-desk-item { position: relative; }

          .ab-desk-card {
            height: var(--dcard-h);
            background: rgba(18,18,18,0.75);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 14px;
            padding: clamp(14px,2vh,22px) clamp(16px,2vw,24px);
            display: flex;
            flex-direction: column;
            box-shadow: 0 8px 32px rgba(0,0,0,0.6);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
            transition: border-color 0.3s ease, background 0.3s ease;
          }
          .ab-desk-card:hover {
            border-color: rgba(236,43,40,0.35);
            background: rgba(22,22,22,0.88);
          }
          .ab-desk-card-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 6px;
          }
          .ab-desk-card-label {
            font-size: 0.54rem;
            letter-spacing: 0.26em;
            text-transform: uppercase;
            font-weight: 700;
            font-family: 'Bricolage Grotesque', sans-serif;
          }
          .ab-desk-card-num {
            font-size: 0.58rem;
            color: rgba(255,255,255,0.15);
            font-family: 'Bricolage Grotesque', sans-serif;
          }
          .ab-desk-card-rule {
            width: 24px; height: 2px;
            border-radius: 2px;
            margin-bottom: clamp(10px,1.4vh,16px);
            flex-shrink: 0;
          }
          .ab-desk-card-heading {
            font-family: 'Bricolage Grotesque', sans-serif;
            font-size: clamp(0.9rem,1.6vw,1.2rem);
            font-weight: 700;
            letter-spacing: -0.03em;
            line-height: 1.2;
            color: #fff;
            margin: 0 0 8px;
            flex-shrink: 0;
          }
          .ab-desk-card-body {
            font-family: 'Bricolage Grotesque', sans-serif;
            font-size: clamp(0.7rem,1vw,0.82rem);
            line-height: 1.65;
            color: rgba(255,255,255,0.44);
            margin: 0;
            flex: 1;
          }
        }

        /* ══════════════════════════
           MOBILE (≤767px) — UNTOUCHED
        ══════════════════════════ */
        @media (max-width:767px) {
          .ab-zone { height:auto !important; }

          .ab-sticky {
            position:relative !important;
            height:auto !important;
            justify-content:flex-start !important;
            padding: clamp(44px,9vh,70px) 20px 0 !important;
            overflow:visible !important;
          }

          .ab-desk-header { display:none !important; }
          .ab-desk-body   { display:none !important; }

          .ab-mobile-header { display:block !important; }

          .ab-blur-1 { animation:blurReveal .9s cubic-bezier(.22,1,.36,1) .1s both; }
          .ab-blur-2 { animation:blurReveal .9s cubic-bezier(.22,1,.36,1) .28s both; }
          .ab-blur-3 { animation:blurReveal .7s cubic-bezier(.22,1,.36,1) .46s both; }

          .ab-mob-list {
            display: grid;
            grid-template-columns: 1fr;
            --card-height: 220px;
            --card-top-offset: 44px;
            grid-template-rows: repeat(3, var(--card-height));
            gap: 0;
            padding: 16px 20px calc(3 * 44px + 40px);
            margin: 0;
            list-style: none;
            background: #0a0a0a;
          }

          .ab-mob-item { position:sticky; top:0; }
          .ab-mob-item:nth-child(1) { padding-top:calc(1 * 44px); z-index:1; }
          .ab-mob-item:nth-child(2) { padding-top:calc(2 * 44px); z-index:2; }
          .ab-mob-item:nth-child(3) { padding-top:calc(3 * 44px); z-index:3; }

          .ab-mob-card {
            height: var(--card-height);
            background: rgba(18,18,18,0.95);
            backdrop-filter: blur(20px) saturate(1.4);
            -webkit-backdrop-filter: blur(20px) saturate(1.4);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 14px;
            padding: 18px 18px;
            display: flex;
            flex-direction: column;
            box-shadow: 0 8px 32px rgba(0,0,0,0.6);
            transform-origin: 50% 0%;
          }
        }
      `}</style>

      <section
        id="about"
        className="ab-zone"
        ref={zoneRef}
        style={{ position: 'relative', background: '#0a0a0a', color: '#fff' }}
      >
        {/* ══ STICKY PANEL ══ */}
        <div className="ab-sticky">

          {/* LightRays bg */}
          <div style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
            <LightRays raysOrigin="top-center" raysColor="#ffffff" raysSpeed={1}
              lightSpread={0.5} rayLength={3} followMouse={true} mouseInfluence={0.1}
              noiseAmount={0} distortion={0} pulsating={false} fadeDistance={1} saturation={1} />
          </div>

          {/* ── DESKTOP: heading ── */}
          <div className="ab-desk-header">
            <h2 className="ab-desk-heading">About <span style={{ color: '#fff' }}>Us</span></h2>
            <span className="ab-desk-foundation">Our Foundation</span>
          </div>

          {/* ── DESKTOP: two-col body ── */}
          <div className="ab-desk-body">

            {/* LEFT — animated text lines */}
            <div className="ab-left-col">
              {ABOUT_LINES.map((ln, i) => (
                <span key={i} className="ab-calc-span" ref={el => (calcRefs.current[i] = el)}>
                  {ln.words.join(' ')}
                </span>
              ))}
              <div className="ab-left-lines">
                {ABOUT_LINES.map((ln, i) => (
                  <div key={i} className="ab-line-wrap" ref={el => (wrapRefs.current[i] = el)}>
                    <div className="ab-visible-line" ref={el => (lineRefs.current[i] = el)}>
                      {ln.words.map((w, j) => (
                        <span key={j}>{w}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT — cards with LaserFlow bg */}
            <div className="ab-right-col">

              {/* LaserFlow — desktop only, behind cards */}
              <div className="ab-right-laser">
                <LaserFlow
                  color="#ec2b28"
                  horizontalBeamOffset={0.0}
                  verticalBeamOffset={0.1}
                  verticalSizing={1.8}
                  horizontalSizing={0.6}
                  wispDensity={0.8}
                  wispIntensity={3.5}
                  wispSpeed={10}
                  fogIntensity={0.35}
                  fogScale={0.25}
                  flowSpeed={0.3}
                  flowStrength={0.2}
                  decay={1.0}
                  falloffStart={1.1}
                />
              </div>

              <ul className="ab-desk-card-list">
                {CARDS.map((c) => (
                  <li key={c.label} className="ab-desk-item">
                    <TiltedCard
                      imageSrc=""
                      altText=""
                      captionText=""
                      containerHeight="var(--dcard-h, 150px)"
                      containerWidth="100%"
                      imageHeight="var(--dcard-h, 150px)"
                      imageWidth="100%"
                      rotateAmplitude={12}
                      scaleOnHover={1.03}
                      showMobileWarning={false}
                      showTooltip={false}
                      displayOverlayContent={true}
                      overlayContent={
                        <div className="ab-desk-card">
                          <div className="ab-desk-card-top">
                            <span className="ab-desk-card-label" style={{ color: c.accent }}>{c.label}</span>
                            <span className="ab-desk-card-num">{c.num}</span>
                          </div>
                          <div className="ab-desk-card-rule" style={{ background: c.accent }} />
                          <h3 className="ab-desk-card-heading">{c.heading}</h3>
                          <p className="ab-desk-card-body">{c.body}</p>
                        </div>
                      }
                    />
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* ── MOBILE HEADER ── */}
          <div className="ab-mobile-header"
            style={{ position: 'relative', zIndex: 1, textAlign: 'center', marginBottom: 10 }}>
            <h2 className="ab-blur-1" style={{
              fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 'clamp(2.4rem, 5.5vw, 4.5rem)',
              fontWeight: 800, margin: '0 0 12px', letterSpacing: '-0.035em',
              lineHeight: 1.08, textTransform: 'none', color: '#ec2b28', textAlign: 'center',
            }}>About Us</h2>
            <p className="ab-blur-2" style={{
              fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 'clamp(0.92rem, 1.25vw, 1.08rem)',
              fontWeight: 400, color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.7, margin: '0 auto', maxWidth: '86%', textAlign: 'center',
            }}>
              Aurelix is a consulting &amp; growth firm. We connect ads, funnels,
              and operations to deliver measurable growth that matters.
            </p>
            <div className="ab-blur-3" style={{
              marginTop: 14, fontSize: '0.52rem', letterSpacing: '0.28em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)',
            }}>Our Foundation</div>
          </div>

        </div>

        {/* ══ MOBILE CARD STACK — untouched ══ */}
        <ul className="ab-mob-list">
          {CARDS.map((c) => (
            <li key={c.label} className="ab-mob-item">
              <div className="ab-mob-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: '0.54rem', letterSpacing: '0.26em', textTransform: 'uppercase', fontWeight: 700, color: c.accent }}>{c.label}</span>
                  <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.15)' }}>{c.num}</span>
                </div>
                <div style={{ width: 24, height: 2, background: c.accent, borderRadius: 2, marginBottom: 12, flexShrink: 0 }} />
                <h3 style={{ fontSize: 'clamp(1rem,5vw,1.2rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.2, color: '#fff', margin: '0 0 10px', flexShrink: 0 }}>{c.heading}</h3>
                <p style={{ fontSize: 'clamp(0.78rem,3.5vw,0.86rem)', lineHeight: 1.65, color: 'rgba(255,255,255,0.45)', margin: 0, flex: 1 }}>{c.body}</p>
              </div>
            </li>
          ))}
        </ul>

      </section>
    </>
  );
}